import { __async, __superGet } from '../chunk-YSKSG3AS.mjs';
import { ConnectorNotFoundError, SwitchChainNotSupportedError } from 'wagmi';
import { UserRejectedRequestError, ResourceUnavailableRpcError, getAddress } from 'viem';
import { InjectedConnector } from 'wagmi/connectors/injected';
import { ParticleNetwork } from '@particle-network/auth';
import { ParticleProvider } from '@particle-network/provider';
import { chains } from '@particle-network/chains';
import { SmartAccount, AAWrapProvider, SendTransactionMode, SendTransactionEvent } from '@particle-network/aa';

var particleProjectId = "fe175fb6-92ba-474c-9257-93114b3886cd";
var particleClientKey = "cuNT3TZhMDN6NjF0K8rYQdcwGUCQKlzauFMXY5dA";
var particleAppId = "82f80966-2bd1-4583-aaf3-44d69d95e972";
var mappingNetwork = {
  43111: {
    name: "hemi-mainnet",
    rpcUrl: "https://rpc.hemi.network/rpc"
  },
  743111: {
    name: "mantle-sepolia",
    rpcUrl: "https://testnet.rpc.hemi.network/rpc"
  }
};
var particleConfig = (chainId) => {
  const chain = chains.getEVMChainInfoById(chainId);
  return {
    projectId: particleProjectId,
    clientKey: particleClientKey,
    appId: particleAppId,
    chainId: chain == null ? void 0 : chain.id,
    chainName: chain == null ? void 0 : chain.name,
    wallet: {
      supportChains: Object.entries(mappingNetwork).map(([key, value]) => ({
        id: parseInt(key),
        name: value.name
      })),
      customStyle: {
        evmSupportWalletConnect: true,
        supportUIModeSwitch: true,
        supportLanguageSwitch: true,
        supportAddToken: true
      }
    },
    securityAccount: {
      // optional: particle security account config
      // prompt set payment password. 0: None, 1: Once(default), 2: Always
      promptSettingWhenSign: 2,
      // prompt set master password. 0: None(default), 1: Once, 2: Always
      promptMasterPasswordSettingWhenLogin: 2
    }
  };
};
var ParticleConnector = class extends InjectedConnector {
  constructor({
    chainInfo: _chainInfo,
    chains: _chains,
    options: _options
  }) {
    var _a, _b;
    const options = {
      name: "Particle Wallet",
      shimDisconnect: (_a = _options == null ? void 0 : _options.shimDisconnect) != null ? _a : false,
      shimChainChangedDisconnect: (_b = _options == null ? void 0 : _options.shimChainChangedDisconnect) != null ? _b : true
    };
    const cchains = _chains == null ? void 0 : _chains.filter((c) => !!mappingNetwork[c.id]);
    super({
      chains: cchains,
      options
    });
    this.id = "Particle";
    this.onAccountsChanged = (accounts) => {
      super.onAccountsChanged(accounts);
      if (this.particleProvider) {
        this.particleProvider.removeListener("disconnect", this.onDisconnect);
        this.particleProvider.removeListener("chainChanged", this.onChainChanged);
        this.particleProvider.removeListener("accountsChanged", this.onAccountsChanged);
      }
    };
    this.onDisconnect = (error) => __async(this, null, function* () {
      yield __superGet(ParticleConnector.prototype, this, "onDisconnect").call(this, error);
      if (this.particleProvider) {
        this.particleProvider.removeListener("disconnect", this.onDisconnect);
        this.particleProvider.removeListener("chainChanged", this.onChainChanged);
        this.particleProvider.removeListener("accountsChanged", this.onAccountsChanged);
      }
    });
    this.chainId = _chainInfo.id;
    this.init = false;
  }
  connect() {
    return __async(this, arguments, function* ({ chainId } = {}) {
      var _a;
      try {
        if (!this.init) {
          try {
            const config = particleConfig(this.chainId);
            console.log("config", config);
            this.particleNetwork = new ParticleNetwork(config);
            this.smartAccount = new SmartAccount(new ParticleProvider(this.particleNetwork.auth), {
              projectId: config.projectId,
              clientKey: config.clientKey,
              appId: config.appId,
              aaOptions: {
                accountContracts: {
                  // 'BICONOMY', 'CYBERCONNECT', 'SIMPLE'
                  BICONOMY: [
                    {
                      version: "1.0.0",
                      chainIds: [...config.wallet.supportChains.map((c) => parseInt(c.id.toString()))]
                    },
                    {
                      version: "2.0.0",
                      chainIds: [...config.wallet.supportChains.map((c) => parseInt(c.id.toString()))]
                    }
                  ],
                  CYBERCONNECT: [
                    {
                      version: "1.0.0",
                      chainIds: [...config.wallet.supportChains.map((c) => parseInt(c.id.toString()))]
                    }
                  ],
                  SIMPLE: [
                    {
                      version: "1.0.0",
                      chainIds: [...config.wallet.supportChains.map((c) => parseInt(c.id.toString()))]
                    }
                  ]
                }
              }
            });
            this.smartAccount.setSmartAccountContract({ name: "BICONOMY", version: "2.0.0" });
            this.init = true;
          } catch (e) {
            this.particleNetwork = void 0;
            this.smartAccount = void 0;
            this.init = false;
            console.error("ParticleNetwork error", e);
          }
        }
        const provider = yield this.getProvider();
        if (!provider) {
          throw new ConnectorNotFoundError();
        }
        if (!this.particleNetwork.auth.isLogin()) {
          yield this.particleNetwork.auth.login();
        }
        if (provider.on) {
          provider.on("accountsChanged", this.onAccountsChanged);
          provider.on("chainChanged", this.onChainChanged);
          provider.on("disconnect", this.onDisconnect);
        }
        this.emit("message", { type: "connecting" });
        const account = yield this.getAccount();
        let id = yield this.getChainId();
        let unsupported = this.isChainUnsupported(id);
        if (chainId && id !== chainId) {
          const chain = yield this.switchChain(chainId);
          id = chain.id;
          unsupported = this.isChainUnsupported(id);
        }
        if (this.options.shimDisconnect)
          (_a = this.storage) == null ? void 0 : _a.setItem(this.shimDisconnectKey, true);
        return { account, chain: { id, unsupported }, provider };
      } catch (error) {
        if (this.isUserRejectedRequestError(error))
          throw new UserRejectedRequestError(error);
        if (error.code === -32002)
          throw new ResourceUnavailableRpcError(error);
        throw error;
      }
    });
  }
  getAccount() {
    return __async(this, null, function* () {
      const provider = yield this.getProvider();
      if (!provider)
        throw new ConnectorNotFoundError();
      const accounts = yield provider.request({
        method: "eth_requestAccounts"
      });
      let account = accounts[0];
      if (typeof account === "string" && !account.startsWith("0x")) {
        account = `0x${account}`;
      }
      return getAddress(account);
    });
  }
  switchChain(chainId) {
    return __async(this, null, function* () {
      const provider = yield this.getProvider();
      if (!provider)
        throw new ConnectorNotFoundError();
      if (mappingNetwork[chainId]) {
        try {
          yield provider.request({
            method: "wallet_switchEthereumChain",
            params: [{
              chainId: `0x${chainId.toString(16)}`
            }]
          });
          return this.chains.find((x) => x.id === chainId);
        } catch (switchError) {
          console.error("switchError", switchError, chainId, `0x${chainId.toString(16)}`);
        }
      }
      throw new SwitchChainNotSupportedError({ connector: this });
    });
  }
  // eslint-disable-next-line class-methods-use-this
  // @ts-ignore
  getProvider() {
    const wrapProvider = new AAWrapProvider(this.smartAccount, SendTransactionMode.UserSelect);
    wrapProvider.once(SendTransactionEvent.Request, (feeQuotesResult) => {
      if (feeQuotesResult.tokenPaymaster && feeQuotesResult.tokenPaymaster.tokenPaymasterAddress && feeQuotesResult.tokenPaymaster.feeQuote[0]) {
        wrapProvider.resolveSendTransaction({
          feeQuote: feeQuotesResult.tokenPaymaster.feeQuote[0],
          tokenPaymasterAddress: feeQuotesResult.tokenPaymaster.tokenPaymasterAddress
        });
        return;
      }
      if (feeQuotesResult.verifyingPaymasterGasless) {
        wrapProvider.resolveSendTransaction(feeQuotesResult.verifyingPaymasterGasless);
        return;
      }
      wrapProvider.resolveSendTransaction(feeQuotesResult.verifyingPaymasterNative);
    });
    return Promise.resolve(wrapProvider);
  }
};

export { ParticleConnector, particleAppId, particleClientKey, particleConfig, particleProjectId };
